import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { getPatientHistoryTrack, resetPatientHistoryTrack } from 'store/thunks/patientThunks';
import PatientHistoryGroup from './PatientHistoryGroup';

const PatientHistoryTrackModal = ({ open, onClose, mpi, patientName }) => {
  const dispatch = useDispatch();
  const historyData = useSelector((state) => state.patient[mpi]?.historyTrack);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const hasMoreData = Boolean(
    historyData?.result?.length && historyData.count > currentPage * itemsPerPage
  );

  const fetchHistoryData = useCallback(
    async (page, append = false) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        await dispatch(getPatientHistoryTrack({ mpi, page, limit: itemsPerPage, append }));
      } catch (error) {
        dispatch(
          handleError({
            error,
            heading: 'Failed to load patient history',
          })
        );
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [dispatch, mpi, itemsPerPage]
  );

  useEffect(() => {
    if (open && mpi) {
      dispatch(resetPatientHistoryTrack({ mpi }));
      setCurrentPage(1);
      fetchHistoryData(1, false);
    }
  }, [open, mpi, dispatch, fetchHistoryData]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchHistoryData(nextPage, true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    setCurrentPage(1);
    onClose();
  };

  const groupedHistory = useMemo(() => {
    const groups = [];
    if (!historyData || isEmpty(historyData.result)) return groups;

    let currentGroup = null;
    historyData.result.forEach((item) => {
      const userKey = `${item.pomUserId || ''}`;
      if (!currentGroup || currentGroup.userKey !== userKey) {
        currentGroup = {
          userKey,
          pomUserFirstName: item.pomUserFirstName,
          pomUserLastName: item.pomUserLastName,
          actorRole: item.actorRole,
          pomUserId: item.pomUserId,
          items: [item],
        };
        groups.push(currentGroup);
      } else {
        currentGroup.items.push(item);
      }
    });

    return groups;
  }, [historyData]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='h6' component='span'>
          Patient History Track - {patientName}
        </Typography>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Grid container justifyContent='center' sx={{ py: 4 }}>
            <LoadingModule />
          </Grid>
        )}

        {!loading && historyData && !isEmpty(historyData.result) && (
          <>
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 20,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  bgcolor: 'divider',
                }}
              />

              {groupedHistory.map((group, index) => (
                <Box
                  key={`${group.userKey}-${group.items[0]?.auditEventId ?? index}`}
                  sx={{
                    pb: index < groupedHistory.length - 1 ? 3 : 0,
                  }}
                >
                  <PatientHistoryGroup group={group} />
                </Box>
              ))}
            </Box>

            {hasMoreData && !loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  startIcon={
                    loadingMore ? <CircularProgress size={20} color='inherit' /> : undefined
                  }
                >
                  {loadingMore ? 'Loading...' : 'View More'}
                </Button>
              </Box>
            )}
          </>
        )}

        {!loading && historyData && isEmpty(historyData.result) && (
          <Grid container justifyContent='center' sx={{ py: 4 }}>
            <NothingFound />
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

PatientHistoryTrackModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mpi: PropTypes.string.isRequired,
  patientName: PropTypes.string.isRequired,
};

export default PatientHistoryTrackModal;
